import { useQuery } from 'convex/react';
import { Id } from '../convex/_generated/dataModel';

/**
 * Hook to get a file URL from a file ID
 */
export const useFileUrl = (fileId?: string | null) => {
  const fileData = useQuery(
    'filesFunctions:getFileUrlFn' as any,
    fileId ? { fileId: fileId as Id<"files"> } : "skip"
  );
  
  return fileData?.url;
};

/**
 * Hook to get a URL directly from storage ID
 */
export const useStorageUrl = (storageId?: string | null) => {
  const urlData = useQuery(
    'filesFunctions:getUrlFromStorageIdFn' as any,
    storageId ? { storageId: storageId as Id<"_storage"> } : "skip"
  );
  
  return urlData?.url;
};

/**
 * Utility function to determine if a URL is a blob URL
 */
export const isBlobUrl = (url?: string | null): boolean => {
  if (!url) return false;
  return url.startsWith('blob:');
};

/**
 * Utility function to get the best available image URL
 * Priority: proper URL > fileId resolved URL > fallback
 */
export const useBestImageUrl = (imageUrl?: string | null, fileId?: string | null, fallback?: string) => {
  const fileUrl = useFileUrl(fileId);
  
  // If we have a proper (non-blob) URL, use it
  if (imageUrl && !isBlobUrl(imageUrl)) {
    return imageUrl;
  }
  
  // If we have a resolved file URL, use it
  if (fileUrl) {
    return fileUrl;
  }
  
  // Fall back to the original URL (even if it's blob) or the fallback
  return imageUrl || fallback;
};

/**
 * Non-hook utility function to get the best available image URL
 * This can be used in non-React contexts or when you already have resolved URLs
 */
export const getBestImageUrl = (imageUrl?: string | null, resolvedFileUrl?: string | null, fallback?: string) => {
  // If we have a proper (non-blob) URL, use it
  if (imageUrl && !isBlobUrl(imageUrl)) {
    return imageUrl;
  }
  
  // If we have a resolved file URL, use it
  if (resolvedFileUrl) {
    return resolvedFileUrl;
  }
  
  // Fall back to the original URL (even if it's blob) or the fallback
  return imageUrl || fallback;
};

/**
 * Hook to resolve multiple file URLs at once for a fixed number of files
 * This version creates hooks for each possible file up to a maximum
 */
export const useMultipleFileUrls = (fileIds: (string | null | undefined)[]) => {
  // Create queries for up to 10 files (should be enough for most use cases)
  const file0Url = useFileUrl(fileIds[0]);
  const file1Url = useFileUrl(fileIds[1]);
  const file2Url = useFileUrl(fileIds[2]);
  const file3Url = useFileUrl(fileIds[3]);
  const file4Url = useFileUrl(fileIds[4]);
  const file5Url = useFileUrl(fileIds[5]);
  const file6Url = useFileUrl(fileIds[6]);
  const file7Url = useFileUrl(fileIds[7]);
  const file8Url = useFileUrl(fileIds[8]);
  const file9Url = useFileUrl(fileIds[9]);
  
  // Array of resolved URLs
  const resolvedUrls = [
    file0Url, file1Url, file2Url, file3Url, file4Url,
    file5Url, file6Url, file7Url, file8Url, file9Url
  ];
  
  // Return only the URLs for the fileIds that were actually provided
  return fileIds.map((_, index) => resolvedUrls[index]);
}; 